import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Building2,
  Calendar,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Calculator,
  MapPin,
  TrendingUp,
  BarChart3,
} from "lucide-react";
import {
  dashboardService,
  DashboardMetrics,
  RecentBooking,
} from "../../services/dashboardService";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const Dashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        const response = await dashboardService.getDashboardMetrics();
        setMetrics(response.data);
      } catch (err) {
        setError("Failed to load dashboard metrics");
        console.error("Error fetching dashboard metrics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("en-IN").format(num);
  };

  // Chart configurations
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: "#6B7280",
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: "#6B7280",
        },
        grid: {
          color: "#E5E7EB",
        },
      },
      y: {
        ticks: {
          color: "#6B7280",
        },
        grid: {
          color: "#E5E7EB",
        },
      },
    },
  };

  // Top Cities Chart Data
  const topCitiesData = {
    labels: metrics?.top_cities?.map((city) => city.city) || [],
    datasets: [
      {
        label: "Bookings",
        data: metrics?.top_cities?.map((city) => city.booking_count) || [],
        backgroundColor: [
          "#3B82F6",
          "#10B981",
          "#F59E0B",
          "#EF4444",
          "#8B5CF6",
          "#06B6D4",
          "#84CC16",
          "#F97316",
          "#EC4899",
          "#6B7280",
        ],
        borderColor: "#1F2937",
        borderWidth: 1,
      },
    ],
  };

  // Hotel Performance Chart Data
  const hotelPerformanceData = {
    labels: metrics?.hotel_performance?.map((hotel) => hotel.hotel_name) || [],
    datasets: [
      {
        label: "Bookings",
        data:
          metrics?.hotel_performance?.map((hotel) => hotel.booking_count) || [],
        backgroundColor: "#3B82F6",
        borderColor: "#1E40AF",
        borderWidth: 1,
      },
    ],
  };

  // Booking Volume Chart Data
  const bookingVolumeData = {
    labels:
      metrics?.booking_volume?.map((volume) => {
        const date = new Date(volume.month + "-01");
        return date.toLocaleDateString("en-IN", {
          month: "short",
          year: "numeric",
        });
      }) || [],
    datasets: [
      {
        label: "Bookings",
        data:
          metrics?.booking_volume?.map((volume) => volume.booking_count) || [],
        borderColor: "#10B981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const kpiData = metrics
    ? [
        {
          title: "Total Revenue (Latest Month)",
          value: formatCurrency(metrics.total_revenue),
          change: "+12.5%", // TODO: Calculate actual change
          trend: "up",
          description: "From confirmed bookings in latest month",
          icon: DollarSign,
          color: "text-green-500",
        },
        {
          title: "Active Hotels",
          value: formatNumber(metrics.active_hotels),
          change: "+8.2%", // TODO: Calculate actual change
          trend: "up",
          description: "Hotels available for booking",
          icon: Building2,
          color: "text-blue-500",
        },
        {
          title: "Total Bookings (Latest Month)",
          value: formatNumber(metrics.total_bookings),
          change: "-3.1%", // TODO: Calculate actual change
          trend: "down",
          description: "Bookings in latest month",
          icon: Calendar,
          color: "text-orange-500",
        },
        {
          title: "Avg Booking Value (Latest Month)",
          value: formatCurrency(metrics.average_booking_value),
          change: "+8.5%", // TODO: Calculate actual change
          trend: "up",
          description: "Per confirmed booking in latest month",
          icon: Calculator,
          color: "text-purple-500",
        },
      ]
    : [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "text-green-500 bg-green-100";
      case "booked (HCN Pending)":
        return "text-sky-500 bg-sky-100";
      case "pending (HBR Pending)":
        return "text-amber-500 bg-amber-100";
      case "cancelled":
        return "text-red-500 bg-red-100";
      case "no_show":
        return "text-orange-500 bg-orange-100";
      case "checked_in":
        return "text-blue-500 bg-blue-100";
      case "checked_out":
        return "text-purple-500 bg-purple-100";
      default:
        return "text-gray-500 bg-gray-100";
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground">
              Loading dashboard metrics...
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="border border-border bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="h-4 bg-muted rounded w-24 animate-pulse"></div>
                <div className="h-4 w-4 bg-muted rounded animate-pulse"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-muted rounded w-20 animate-pulse mb-2"></div>
                <div className="h-4 bg-muted rounded w-16 animate-pulse mb-1"></div>
                <div className="h-3 bg-muted rounded w-32 animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-destructive">{error}</p>
          </div>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
        </div>
        <Button>
          <Calendar className="w-4 h-4 mr-2" />
          View Reports
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpiData.map((kpi, index) => {
          const Icon = kpi.icon;
          return (
            <Card key={index} className="border border-border bg-white">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {kpi.title}
                </CardTitle>
                <Icon className={`w-4 h-4 ${kpi.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {kpi.value}
                </div>
                <div className="flex items-center space-x-2 mt-2">
                  {kpi.trend === "up" ? (
                    <ArrowUpRight className="w-4 h-4 text-green-500" />
                  ) : (
                    <ArrowDownRight className="w-4 h-4 text-red-500" />
                  )}
                  <span
                    className={`text-sm ${
                      kpi.trend === "up" ? "text-green-500" : "text-red-500"
                    }`}
                  >
                    {kpi.change}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {kpi.description}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Cities by Bookings */}
        <Card className="border border-border bg-white">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-primary" />
              Top Cities by Bookings (Latest Month)
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Most popular destinations in latest month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Doughnut data={topCitiesData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>

        {/* Hotel Performance */}
        <Card className="border border-border bg-white">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-emerald-500" />
              Hotel Performance (Latest Month)
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Bookings by hotel in latest month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <Bar data={hotelPerformanceData} options={chartOptions} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Booking Volume Chart */}
      <Card className="border border-border bg-white">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-emerald-500" />
            Booking Volume by Month
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Monthly booking trends
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <Line data={bookingVolumeData} options={chartOptions} />
          </div>
        </CardContent>
      </Card>

      {/* Recent Bookings */}
      <Card className="border border-border bg-white">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-primary" />
            Recent Bookings
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            Latest hotel bookings and their status
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {metrics?.recent_bookings?.map((booking) => (
              <div
                key={booking.id ?? booking.booking_reference}
                className="flex items-center justify-between p-3 bg-secondary rounded-lg"
              >
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-foreground font-medium text-sm truncate">
                      {booking.hotel_name}
                    </p>
                    <p className="text-muted-foreground text-xs truncate">
                      {booking.guest_name}
                    </p>
                    <p className="text-muted-foreground text-xs truncate">
                      Ref: {booking.booking_reference}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {formatDate(booking.checkin_date)} -{" "}
                      {formatDate(booking.checkout_date)}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-foreground font-medium text-sm">
                    {formatCurrency(booking.total_amount)}
                  </p>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                      booking.booking_status
                    )}`}
                  >
                    {booking.booking_status.replace("_", " ")}
                  </span>
                </div>
              </div>
            )) || (
              <div className="text-center py-6">
                <p className="text-muted-foreground text-sm">
                  No recent bookings found
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
